import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import type { Dispatch } from 'redux'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type {
    FaqShortDTO,
    FormFaqShortDTO,
    ThemeShortDTO
} from 'shared/types/api'
import type { RootState } from 'shared/store/reducers'
import type { FaqForm } from './types'
import { encode as btoa } from 'base-64'
import { encode } from 'utf8'

type ActionPageDataFaqResponse = {
    type: 'PAGE_DATA_FAQ_RESPONSE',
    data: FaqShortDTO
}

type ActionFaqFormDataResponse = {
    type: 'FAQ_FORM_DATA_RESPONSE',
    formData: FormFaqShortDTO
}

type ActionFaqCategorySelected = {
    type: 'FAQ_CATEGORY_SELECTED',
    selectedCategory: string
}

type ActionFaqThemeSelected = {
    type: 'FAQ_THEME_SELECTED',
    selectedTheme: ThemeShortDTO
}

type ActionFaqMoreQuestions = {
    type: 'FAQ_MORE_QUESTIONS'
}

type ActionFaqFormPostSuccess = {
    type: 'FAQ_FORM_POST_SUCCESS',
    postedData: any
}

type ActionFaqResetSuccess = {
    type: 'FAQ_FORM_RESET_SUCCESS'
}

type ActionFaqFormPostError = {
    type: 'FAQ_FORM_POST_ERROR',
    error: any
}

type ActionFaqError = {
    type: 'FAQ_ERROR',
    error: boolean
}

type GetState = () => RootState

const fetchFaqPageData = () => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        dispatch(fetchPageData(endpoints.pages.faq, 'PAGE_DATA_FAQ_RESPONSE'))
        // TODO: Manage router for rect native
        const router = getState().router
        const search = router ? router.location.search : ''
        const params = search.split('&')
        if (
            search.includes('label=') &&
            search.includes('notation=') &&
            search.includes('restaurant=') &&
            search.includes('order=') &&
            search.includes('kingdom=') &&
            search.includes('mandatoryReceipt=') &&
            search.includes('receiptMention=') &&
            search.includes('ecommerce=') &&
            params.length === 8
        ) {
            const label = params[0].substring(7).replace('%20', ' ')
            const notation = params[1].split('=') === 'true'
            const restaurant = params[2].split('=') === 'true'
            const order = params[3].split('=') === 'true'
            const kingdom = params[4].split('=') === 'true'
            const mandatoryReceipt = params[5].split('=') === 'true'
            const receiptMention = params[6].substring(7).replace('%20', ' ')
            const ecommerce = params[7].split('=') === 'true'
            const selectedTheme = {
                label,
                notation,
                restaurant,
                order,
                kingdom,
                mandatoryReceipt,
                receiptMention,
                ecommerce
            }
            dispatch(applySelectedTheme(selectedTheme))
        }
    }
}

const actionFaqFormDataResponse = (
    formData: FormFaqShortDTO
): ActionFaqFormDataResponse => ({
    type: 'FAQ_FORM_DATA_RESPONSE',
    formData
})

const applySelectedCategory = (
    selectedCategory: string
): ActionFaqCategorySelected => ({
    type: 'FAQ_CATEGORY_SELECTED',
    selectedCategory
})

const applySelectedTheme = (
    //$FlowFixMe
    selectedTheme: ThemeShortDTO
): ActionFaqThemeSelected => ({
    type: 'FAQ_THEME_SELECTED',
    selectedTheme
})

const actionSuccess = (postedData: any): ActionFaqFormPostSuccess => ({
    type: 'FAQ_FORM_POST_SUCCESS',
    postedData
})

const resetSuccess = (): ActionFaqResetSuccess => ({
    type: 'FAQ_FORM_RESET_SUCCESS'
})

const actionError = (error: any): ActionFaqError => ({
    type: 'FAQ_ERROR',
    error
})

const actionFormPostError = (error: any): ActionFaqFormPostError => ({
    type: 'FAQ_FORM_POST_ERROR',
    error
})

const moreQuestions = (): ActionFaqMoreQuestions => ({
    type: 'FAQ_MORE_QUESTIONS'
})

const selectTheme = (themeLabel: string) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const { formData, themes, selectedTheme } = getState().faqPageState
        if (themes) {
            const theme = themes.find(
                (t: ThemeShortDTO) => t.label === themeLabel
            )
            if (theme && theme !== selectedTheme) {
                dispatch(applySelectedTheme(theme))
            }
        }
        if (formData) {
            return
        }
        return ws
            .get(endpoints.faq.formData)
            .then(response => {
                dispatch(actionFaqFormDataResponse(response.data))
            })
            .catch(error => {
                dispatch(actionError(error))
            })
    }
}

const sendFaqFormData = (form: FaqForm, path: string) => async (
    dispatch: Dispatch<any>
) => {
    let data = new FormData()
    try {
        for (let key in form) {
            if (form[key]) {
                let value = form[key]
                if (
                    [
                        'civility',
                        'date',
                        'note',
                        'receipt',
                        'receiptExtension',
                        'themeLabel',
                        'zipCode',
                        'loyaltyCardCode',
                        'email',
                        'phone',
                        'amount'
                    ].indexOf(key) < 0
                ) {
                    value = btoa(encode(form[key]))
                }
                data.append(key, value)
            }
        }
        data.append('encoded', 'true')
        /* TODO: REFACTO WHEN WE HAVE TIME by using ws from axios */
        let options = {
            method: 'POST',
            url: `${endpoints.faq.send}/${path}`,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 60000,
            data
        }
        await ws(options)
        dispatch(actionSuccess(form))
    } catch (error) {
        console.error('Error sending FAQ form', error)
        dispatch(actionFormPostError(error))
    }
}

export {
    fetchFaqPageData,
    applySelectedCategory,
    applySelectedTheme,
    moreQuestions,
    sendFaqFormData,
    selectTheme,
    actionError,
    actionFormPostError,
    resetSuccess
}

export type Action =
    | ActionPageDataFaqResponse
    | ActionFaqFormDataResponse
    | ActionFaqCategorySelected
    | ActionFaqThemeSelected
    | ActionFaqMoreQuestions
    | ActionFaqFormPostSuccess
    | ActionFaqFormPostError
    | ActionFaqError
    | ActionsNetwork
    | ActionFaqResetSuccess
